import { Box, Flex, Title } from "@mantine/core"
import { useState } from "react"
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Area,
} from "recharts"

import { LabelsExplorerResponses } from "@costory/types/endpoints/labelsExplorer"

import { tickFormatter } from "@costory/shared/utils/charts"
import { formatNumber } from "@costory/shared/utils/format"

import { SmartLegend } from "@costory/front/components/SmartLegend"
import { ChartTooltip } from "@costory/front/components/charts/ChartTooltip"
import { useChartColors } from "@costory/front/components/common/useChartColors"
import { getCharteSerieColor } from "@costory/front/utils/charts"

export const CostBreakdownChart = ({
  label,
}: {
  label: LabelsExplorerResponses.LabelsStatsFullBilling
}) => {
  const { axisTextStyle } = useChartColors()
  const [hideArea, setHideArea] = useState<Set<string>>(new Set())
  const uniqueKeys = Array.from(
    label.months.reduce((set, monthObj) => {
      Object.keys(monthObj).forEach((key) => {
        if (key !== "date" && key !== "month") {
          set.add(key)
        }
      })
      return set
    }, new Set<string>()),
  )

  const allDates = Array.from(new Set(label.months.map((m) => m.date))).sort()
  const stackedData = allDates.map((date) => {
    const row: { month: number | Date; [key: string]: number | Date } = {
      month: date,
    }
    const monthObj = label.months.find((m) => m.date === date) || {}
    uniqueKeys.forEach((key) => {
      row[key] = monthObj[key] ?? 0
    })
    return row
  })

  const finalValues = uniqueKeys.map((key, index) => ({
    value: key,
    color: getCharteSerieColor(index),
    dataKey: key,
  }))
  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        w="100%"
        wrap="nowrap"
        miw={0}
        mb={5}
      >
        <Title order={4}>Cost Breakdown Over Time</Title>
        <Box>
          <SmartLegend
            onEntryClick={(entry) => {
              setHideArea((prev) => {
                const newSet = new Set(prev)
                if (newSet.has(entry.dataKey)) {
                  newSet.delete(entry.dataKey)
                } else {
                  newSet.add(entry.dataKey)
                }
                return newSet
              })
            }}
            hiddenSeries={hideArea}
            payload={finalValues}
            maxVisibleLegends={2}
          />
        </Box>
      </Flex>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart margin={{ right: 50, top: 10 }} data={stackedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12 }}
            angle={30}
            height={60}
            textAnchor="start"
            fontSize={12}
            tickFormatter={(val: string) => tickFormatter(val, "Month")}
            style={axisTextStyle}
          />
          <YAxis
            tickFormatter={(value) => formatNumber({ value })}
            tick={{ fontSize: 12 }}
            style={axisTextStyle}
          />
          <Tooltip
            content={({ label, payload, coordinate }) => (
              <ChartTooltip
                isLabelexplorerChart={true}
                label={label}
                payload={
                  payload as {
                    name: string
                    value: number
                    dataKey: string
                    color: string
                  }[]
                }
                currency="USD"
                coordinate={{
                  x: coordinate?.x ?? 0,
                }}
                aggBy="Month"
              />
            )}
          />
          {finalValues.map((value) => (
            <Area
              hide={hideArea.has(value.value)}
              key={value.value}
              type="monotone"
              dataKey={value.value}
              stackId="1"
              stroke={value.color}
              fill={value.color}
              fillOpacity={0.7}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </>
  )
}
